import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { Api } from '../api/api';
import { Track } from '../../models/interfaces/track.interface';
import { Course } from '../../models/interfaces/course.interface';
import { createCrudUrl, CrudRequestParams } from '../pagination/models/interfaces/crud';
import { PaginationDto } from '../pagination/models/interfaces/pagination-dto';
import { getCrudErrors } from '../../utils/functions';
import { CourseService } from '../course/course.service';
import { tracksMock } from '../../mocks/interfaces/track.mock';
import { applyMockCrudParams } from '../../utils/mock-crud.util';

@Injectable({
  providedIn: 'root',
})
export class TrackService {
  constructor(private readonly courseService: CourseService) {}

  private readonly api: Api = new Api();

  private tracks: Track[] = structuredClone(tracksMock);

  //#region Real API

  public async getAll(): Promise<Track[]> {
    if (environment.useFakeBackend) {
      return this.getAllMocked();
    }

    const url = environment.routes.tracks.base;
    const { error, success } = await this.api.getAsync<Track[]>(url);

    if (!success)
      throw new Error(getCrudErrors(error)[0]);

    return success;
  }

  public async search(params: CrudRequestParams<Track>): Promise<Track[]> {
    if (environment.useFakeBackend) {
      return this.searchMocked(params);
    }

    const url = createCrudUrl<Track>(environment.routes.tracks.search, params);
    const { error, success } = await this.api.getAsync<PaginationDto<Track>>(url);

    if (!success)
      throw new Error(getCrudErrors(error)[0]);

    const tracks = success.data;

    if (!this.shouldHydrateCourses(params)) {
      return tracks;
    }

    return await this.attachTrackCourses(tracks);
  }

  public async getAllByUser(params?: CrudRequestParams<Track>): Promise<Track[]> {
    if (environment.useFakeBackend) {
      return this.getAllByUserMocked(params);
    }

    if (params) {
      return this.search(params);
    }

    return this.getAll();
  }

  public async getById(id: string, params?: CrudRequestParams<Track>): Promise<Track | undefined> {
    if (environment.useFakeBackend) {
      return this.getByIdMocked(id, params);
    }

    const url = createCrudUrl<Track>(environment.routes.tracks.one(id), params);
    const { error, success } = await this.api.getAsync<Track>(url);

    if (!success)
      throw new Error(getCrudErrors(error)[0]);

    if (!this.shouldHydrateCourses(params)) {
      return success;
    }

    const [withCourses] = await this.attachTrackCourses([success]);
    return withCourses;
  }

  //#endregion

  //#region Fake Backend

  public async getAllMocked(): Promise<Track[]> {
    return [...this.tracks];
  }

  public async searchMocked(params: CrudRequestParams<Track> = {}): Promise<Track[]> {
    const tracks = applyMockCrudParams(this.tracks, params);

    if (!this.shouldHydrateCourses(params)) {
      return tracks;
    }

    return this.attachTrackCoursesMocked(tracks);
  }

  public async getAllByUserMocked(params?: CrudRequestParams<Track>): Promise<Track[]> {
    if (params) {
      return this.searchMocked(params);
    }

    return this.getAllMocked();
  }

  public async getByIdMocked(id: string, params?: CrudRequestParams<Track>): Promise<Track | undefined> {
    const track = this.tracks.find(item => item.id === id);

    if (!track) {
      return undefined;
    }

    if (!this.shouldHydrateCourses(params)) {
      return { ...track };
    }

    const [withCourses] = await this.attachTrackCoursesMocked([track]);
    return withCourses;
  }

  public resetMockData(): void {
    this.tracks = structuredClone(tracksMock);
  }

  //#endregion

  private shouldHydrateCourses(params?: CrudRequestParams<Track>): boolean {
    return Array.isArray(params?.join) && params.join.includes('courses');
  }

  private async attachTrackCourses(tracks: Track[]): Promise<Track[]> {
    if (!tracks.length) {
      return tracks;
    }

    const linksByTrackId = new Map<string, string[]>();
    const courseIds = new Set<string>();

    for (const track of tracks) {
      const { error: linkError, success: linkSuccess } = await this.api.getAsync<{ trackId: string; courseId: string }[]>(
        environment.routes.tracks.courses(track.id),
      );

      if (!linkSuccess)
        throw new Error(getCrudErrors(linkError)[0]);

      const ids = linkSuccess.map(link => link.courseId);

      linksByTrackId.set(track.id, ids);
      ids.forEach(id => courseIds.add(id));
    }

    if (!courseIds.size) {
      return tracks.map(track => ({ ...track, courses: [] }));
    }

    const coursesUrl = createCrudUrl<Course>(environment.routes.courses.search, {
      search: { id: { $in: [...courseIds] } },
    });
    const { error: coursesError, success: coursesSuccess } = await this.api.getAsync<PaginationDto<Course>>(coursesUrl);

    if (!coursesSuccess)
      throw new Error(getCrudErrors(coursesError)[0]);

    const enrichedCourses = await this.courseService.enrichCoursesMetadata(coursesSuccess.data);
    return this.mapTracksWithCourseLookup(tracks, linksByTrackId, enrichedCourses);
  }

  private async attachTrackCoursesMocked(tracks: Track[]): Promise<Track[]> {
    if (!tracks.length) {
      return tracks;
    }

    const linksByTrackId = new Map<string, string[]>(
      tracks.map(track => [track.id, track.coursesIds ?? []]),
    );
    const courseIds = new Set<string>();
    linksByTrackId.forEach(ids => ids.forEach(id => courseIds.add(id)));

    if (!courseIds.size) {
      return tracks.map(track => ({ ...track, courses: [] }));
    }

    const courses = await this.courseService.search({
      search: { id: { $in: [...courseIds] } },
    });

    return this.mapTracksWithCourseLookup(tracks, linksByTrackId, courses);
  }

  private mapTracksWithCourseLookup(
    tracks: Track[],
    linksByTrackId: Map<string, string[]>,
    courses: Course[],
  ): Track[] {
    const courseById = new Map(courses.map(course => [course.id, course]));

    return tracks.map(track => {
      const trackCourseIds = linksByTrackId.get(track.id) ?? [];

      return {
        ...track,
        courses: trackCourseIds
          .map(courseId => courseById.get(courseId))
          .filter((course): course is Course => !!course),
      };
    });
  }
}
